import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { 
  ExclamationTriangleIcon,
  ArrowPathIcon,
  GlobeAltIcon 
} from '@heroicons/react/24/outline';

const NetworkStatus = () => {
  const { user, loading: authLoading } = useAuth();
  const [networkStatus, setNetworkStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);

  const loadNetworkStatus = useCallback(async () => {
    // Only load if user is authenticated and auth loading is complete
    if (!user || authLoading) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.get('/network/fbr-compliance');
      setNetworkStatus(response.data.data);
    } catch (error) {
      // Only log and show errors if it's not an authentication issue
      if (error.response?.status !== 401) {
        console.error('Failed to load network status:', error);
        toast.error('Failed to load network status');
      }
    } finally {
      setLoading(false);
    }
  }, [user, authLoading]);

  const verifyIP = async () => {
    // Only verify if user is authenticated and auth loading is complete
    if (!user || authLoading) return;

    try {
      setVerifying(true);
      const response = await apiClient.get('/network/verify-ip');
      const result = response.data.data;
      
      if (result.isValid) {
        toast.success('IP verification successful!');
      } else {
        toast.error('IP verification failed!');
      }
      
      // Reload status after verification
      await loadNetworkStatus();
    } catch (error) {
      // Only log and show errors if it's not an authentication issue
      if (error.response?.status !== 401) {
        console.error('IP verification failed:', error);
        toast.error('IP verification failed');
      }
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    // Only load network status when auth loading is complete and user is authenticated
    if (!authLoading && user) {
      loadNetworkStatus();
    }
  }, [user, authLoading, loadNetworkStatus]);

  // Don't render the component if user is not authenticated or auth is still loading
  if (authLoading || !user) {
    return null;
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (!networkStatus) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="text-center text-gray-500">
          <ExclamationTriangleIcon className="h-12 w-12 mx-auto mb-4" />
          <p>Failed to load network status</p>
          <button
            onClick={loadNetworkStatus}
            className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <GlobeAltIcon className="h-5 w-5 mr-2" />
          FBR Network Compliance
        </h3>
        <button
          onClick={verifyIP}
          disabled={verifying}
          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-4 w-4 mr-2 ${verifying ? 'animate-spin' : ''}`} />
          {verifying ? 'Verifying...' : 'Verify IP'}
        </button>
      </div>

      {/* Compliance Status */}
      

      
    </div>
  );
};

export default NetworkStatus;
